/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2017, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'./BaseProvider',
	'../../../DynamicFileLoader'
], function(_, BaseProvider, DynamicFileLoader) {

	const ID_prefix = 'com.ibm.bi.dashboard.';
	const ICON_REG_EXP = /{{ICON:.*}}/;

	const LocalProvider = BaseProvider.extend({

		definitionsPromise: null,

		init: function(options) {
			LocalProvider.inherited('init', this, arguments);
			this.logger = options.dashboardApi.getGlassCoreSvc('.Logger');
			this.localDefinitions = [];
		},

		clear: function() {
			this.definitionsPromise = null;
		},

		/**
		 * this helper function will traverse the entire definition and replace {{ICON:....}} with the right icon id.
		 * e.g. for "{{ICON:restore_16.default.id}}", it is equivalent as:
		 *     const module = require('dashboard-analytics/lib/@waca/dashboard-common/dist/lib/@ba-ui-toolkit/ba-graphics/dist/icons-js/restore_16');
		 *     const iconId = module['default']['id']
		 */
		_fetchIconId(definitionObj) {
			const proms = [];
			for (const key in definitionObj) {
				if (typeof definitionObj[key] == 'object' && definitionObj[key] !== null) {
					proms.push(...this._fetchIconId(definitionObj[key]));
				} else if (typeof definitionObj[key] === 'string' && ICON_REG_EXP.test(definitionObj[key])) {
					let iconPath = definitionObj[key].slice(7, -2).split('.');
					const filename = iconPath[0];
					const paths = iconPath.slice(1);
					const url = `dashboard-analytics/lib/@waca/dashboard-common/dist/lib/@ba-ui-toolkit/ba-graphics/dist/icons-js/${filename}`;
					proms.push(
						DynamicFileLoader.load([url]).then(modules=>{
							let m = modules[0];
							paths.forEach(n=>{
								m = m[n];
							});
							definitionObj[key] = m;
						})
					);
				}
			}
			return proms;
		},

		_preprocessDefinitions(definitionList) {
			const proms = [];
			definitionList.forEach(definition=>{
				// fix any fully qualified ids to local ids
				definition.id = definition.id.replace(ID_prefix, '');

				// replace {{ICON:****}} with the correct id
				proms.push(...this._fetchIconId(definition));
			});
			return Promise.all(proms).then(()=>{
				return definitionList;
			});
		},

		getDefinitions: function() {
			if (!this.definitionsPromise) {
				this.definitionsPromise = new Promise((resolve) => {
					this.dashboardApi.findGlassCollection(ID_prefix + 'live-visualizations')
						.then((data) => {
							return this._preprocessDefinitions(data);
						}).then(definitionList => {

							this.setDefinitions(definitionList);
							this.massageDefinitions(definitionList);
							this.freezeDefinitions(definitionList);

							definitionList = definitionList || [];
							this.localDefinitions.push(...definitionList);
							resolve(this.localDefinitions);
						})
						.catch((e) => {
							this.logger.error('Error: unable to query local definitions', e);
							resolve([]);
						});
				});
			}
			return this.definitionsPromise;
		},

		massageDefinitions: function(definitions = []) {
			definitions.forEach(definition => {
				const datasets = definition.datasets;
				if (datasets) {
					datasets.forEach(dataset => {
						dataset.getId = () => {
							return dataset.name;
						};
					});
				}
			});
		},

		refreshProvider() {
			return Promise.resolve(this.localDefinitions);
		},

		refresh()  {
			throw new Error('refresh is not supported for local provider');
		}
	});

	return LocalProvider;
});
